import { useNavigation } from '@react-navigation/native';
import { useStoreActions, useStoreState } from '@/stores/store';
import { useCallback, useState } from 'react';
import { Dimensions, FlatList, Image, StyleSheet, View } from 'react-native';
import { Text } from '@/components/Text';
import { useNotification } from '@/components/ui/therapist/Notification/NotificationProvider';
import api from '../../api/axios';
import { copyExcerciseSet } from '../../api/services/user';
import avatars from '../../assets/avatars';
import colors, { appTheme } from '@/config/colors';
import { useDashboardContext } from '@/contexts/DashboardContext/useDashboardContext';
import encryptedStorage from '../../stores/encryptedStorage';

import {
  SetCardOptions,
  SCREEN_NAME_ACCORD_TYPE,
  API_NAME_ACCORD_TYPE,
} from './SetCardOptions';

import { IconButton } from '@/components/Button';
import { PressableNeolexon } from '@/components/PressableNeolexon';
import LoadingIndicator from '@/components/SharedComponents/LoadingIndicator/LoadingIndicator';
import { hasInternetAsync } from './helpers';
import { logError, logInfo } from '@/utils/logHandling';
import { Modal } from '@/components/ui/therapist/Modal';
import Card from '@/components/ui/therapist/Card';
import { Button } from '@/components/ui/therapist/Button';

const DeleteModalDataAccordingToSetType = {
  Wörtersets: {
    title: 'Wörterset löschen',
    activeSetText: (patientName) => {
      return `ACHTUNG! Sie sind dabei ein aktives Wörterset zu löschen! Danach kann ${patientName} das Wörterset nicht mehr im Eigentraining verwenden. Das Löschen des Wörtersets inklusive dessen Ergebnisse ist unwiderruflich!`;
    },
    bodyText:
      'Das Löschen des Wörtersets inklusive dessen Ergebnisse ist unwiderruflich!',
    confirmText: 'Unwiderruflich löschen',
    cancelText: 'Abbrechen',
  },
  Sätzesets: {
    title: 'Sätzeset löschen',
    activeSetText: (patientName) => {
      return `ACHTUNG! Sie sind dabei ein aktives Sätzeset zu löschen! Danach kann ${patientName} das Sätzeset nicht mehr im Eigentraining verwenden. Das Löschen des Sätzesets inklusive dessen Ergebnisse ist unwiderruflich!`;
    },
    bodyText:
      'Das Löschen des Sätzesets inklusive dessen Ergebnisse ist unwiderruflich!',
    confirmText: 'Unwiderruflich löschen',
    cancelText: 'Abbrechen',
  },
  Textesets: {
    title: 'Texteset löschen',
    activeSetText: (patientName) => {
      return `ACHTUNG! Sie sind dabei ein aktives Texteset zu löschen! Danach kann ${patientName} das Texteset nicht mehr im Eigentraining verwenden. Das Löschen des Textesets ist unwiderruflich!`;
    },
    bodyText:
      'Das Löschen des Textesets inklusive dessen Ergebnisse ist unwiderruflich!',
    confirmText: 'Unwiderruflich löschen',
    cancelText: 'Abbrechen',
  },
};

const ACTIVE_SETS_TYPE = {
  Wörtersets: 'activeWordsets',
  Sätzesets: 'activeSentencesets',
  Textesets: 'activeTextsets',
};

export function SetCard({
  data,
  type,
  patientID,
  fetchPatientsWrapper,
  selectedPatientFullName,
  patientsSyncing,
  indexSetCard,
  refreshPatient,
}) {
  const {
    patients,
    changeID,
    selectedID,
    selectedPatient,
    mounted,
    toggleRefreshOnCopySets,
    setAllowefreshOnCopySets,
  } = useDashboardContext();
  const [deleteModal, setDeleteModal] = useState({
    show: false,
    isSetActive: false,
  });

  const { name, _id, appDisplayName } = data ?? {};

  const { navigate } = useNavigation();
  const { showNotification } = useNotification();
  const [showCopyModal, setShowCopyModal] = useState(false);
  const [infoCopyModal, setInfoCopyModal] = useState({
    title: '',
    idOfDataSet: '',
    type: '',
  });
  const [showCopyErrorModal, setShowCopyErrorModal] = useState(false);
  const [localSelectedPatientID, setLocalSelectedPatientID] =
    useState(selectedID);
  const { setGlobalPopupState } = useStoreActions((actions) => ({
    setGlobalPopupState: actions.setGlobalPopupState,
  }));
  const { isTherapistDemo } = useStoreState((store) => store);

  const handleDeletePress = useCallback(async () => {
    try {
      await api.delete(
        `/api/patients/${patientID}/${API_NAME_ACCORD_TYPE[type]}/${data?._id}`,
        data,
      );
      await encryptedStorage().removeItem(data?._id);
      fetchPatientsWrapper();
      refreshPatient();
      setDeleteModal({
        show: false,
        isSetActive: false,
      });
    } catch (error) {
      logError('Could not delete patient', error);
    }
  }, []);

  const onActionPress = (name, activePatient) => {
    return async () => {
      const internet = await hasInternetAsync();
      if (internet) {
        switch (name) {
          case 'trash': {
            if (isTherapistDemo) {
              showNotification({
                type: 'error',
                title: 'Löschen in der Demo nicht möglich',
                position: 'top',
              });
            } else {
              const activeSets = activePatient[ACTIVE_SETS_TYPE[type]];
              const isActiveSet = activeSets.includes(_id);
              setDeleteModal({
                show: true,
                isSetActive: isActiveSet,
              });
            }
            break;
          }
          case 'pencil': {
            navigate(SCREEN_NAME_ACCORD_TYPE[type], {
              selectedID: patientID,
              setId: data._id,
              apiName: API_NAME_ACCORD_TYPE[type],
            });
            break;
          }
          case 'copy': {
            if (isTherapistDemo) {
              showNotification({
                type: 'error',
                title: 'Kopieren in der Demo nicht möglich',
                position: 'top',
              });
            } else {
              let typeTitle = '';
              const endsWithS = type.endsWith('s');
              if (endsWithS) {
                typeTitle = type.slice(0, -1);
              } else {
                typeTitle = type;
              }
              setInfoCopyModal({
                title: typeTitle,
                idOfDataSet: data._id,
                type: type,
              });
              setShowCopyModal(true);
            }
            break;
          }
          default: {
            logInfo('ACTION BTN PRESSED');
          }
        }
      } else {
        setGlobalPopupState({
          headerText: 'Keine Verbindung zum neolexon Server',
          bodyText:
            'Überprüfen Sie Ihre Internetverbindung und versuchen es erneut.',
          leftButton: '',
          rightButton: '',
        });
      }
    };
  };

  const hideCopyModal = useCallback(() => {
    setShowCopyModal(false);
  }, []);

  const copyPatient = useCallback(async (userId, setId, type) => {
    try {
      let set = '';
      let setTitle = '';
      if (type === 'Sätzesets') {
        set = 'sentencesets';
        setTitle = 'Sentenceset';
      } else if (type === 'Wörtersets') {
        set = 'wordsets';
        setTitle = 'Wordset';
      }
      const res = await copyExcerciseSet(userId, setId, set, setTitle);
      if (
        res?.data?.result === 'ok' ||
        res?.data?.message?.includes('success copy to')
      ) {
        setShowCopyModal(false);
        setInfoCopyModal({ title: '', idOfDataSet: '', type: '' });
        changeID(userId, true, patients);
        setAllowefreshOnCopySets(true);
        toggleRefreshOnCopySets();
        const patient = patients.find((p) => p._id === userId);
        showNotification({
          type: 'success',
          title: `Set erfolgreich an ${patient.firstName} ${patient.lastName} kopiert`,
        });
      } else {
        throw new Error('');
      }
    } catch (_error) {
      setShowCopyErrorModal(true);
    }
  }, []);

  const cardStyle = {
    paddingHorizontal: 10,
    flex: 1,
    maxHeight: '90%',
    minHeight: patients.length > 3 ? 200 : 150,
  };

  const deleteModalData = DeleteModalDataAccordingToSetType[type];

  return (
    <>
      <Modal
        isVisible={deleteModal.show}
        onClose={() =>
          setDeleteModal((prev) => ({
            ...prev,
            show: false,
          }))
        }
        cancelActionButton={
          <Button
            label={deleteModalData.cancelText}
            look="secondary"
            onPress={() =>
              setDeleteModal((prev) => ({
                ...prev,
                show: false,
              }))
            }
          />
        }
        confirmActionButton={
          <Button
            label={deleteModalData.confirmText}
            look="danger"
            onPress={handleDeletePress}
          />
        }
        title={deleteModalData.title}
      >
        <Text style={{ fontSize: 20 }}>
          {deleteModal.isSetActive
            ? deleteModalData.activeSetText(selectedPatientFullName)
            : deleteModalData.bodyText}
        </Text>
      </Modal>

      <Card title={name} style={styles.card}>
        <View style={styles.controlWrapper}>
          <IconButton
            onPress={onActionPress('pencil')}
            icon="edit"
            label="bearbeiten"
          />
          <IconButton
            onPress={onActionPress('copy')}
            icon="copy"
            label="kopieren"
          />
          <IconButton
            onPress={onActionPress('trash', selectedPatient)}
            icon="trash"
            label="löschen"
          />
        </View>

        <SetCardOptions
          _id={_id}
          patientsSyncing={patientsSyncing}
          type={type}
          exerciseSetName={name}
          appDisplayName={appDisplayName}
          indexSetCard={indexSetCard}
        />

        {!mounted && <LoadingIndicator showLoading />}
      </Card>

      <Modal
        customStyle={{ maxHeight: 500, flex: 1 }}
        isVisible={showCopyModal}
        onClose={hideCopyModal}
        cancelActionButton={
          <Button label="Abbrechen" onPress={hideCopyModal} look="secondary" />
        }
        confirmActionButton={
          <Button
            label="Kopieren"
            onPress={() =>
              copyPatient(
                localSelectedPatientID,
                infoCopyModal.idOfDataSet,
                infoCopyModal.type,
              )
            }
          />
        }
        title={`${infoCopyModal.title} kopieren`}
      >
        <View style={styles.maxWidth}>
          <View style={styles.underline} />
          <View style={cardStyle}>
            <Text style={styles.titleTwo}>
              Zu welchem Patienten-Profil möchten Sie das Übungsset kopieren?
            </Text>
            <FlatList
              enableEmptySections={true}
              data={patients}
              keyExtractor={(item) => item._id}
              numColumns={3}
              renderItem={({ item }) => {
                const isSelected = localSelectedPatientID == item._id;
                const name = item.firstName + ' ' + item.lastName;
                const imageFromDB = item.avatar[0]
                  .split('img')[1]
                  .split('/')[2]
                  .split('.')[0];
                const image = avatars[imageFromDB];
                const idStyles = StyleSheet.create({
                  backgroundColor: {
                    backgroundColor: isSelected
                      ? appTheme.primary
                      : colors.white,
                    borderColor: isSelected ? 'transparent' : colors.black,
                    borderWidth: 1,
                  },
                });
                return (
                  <PressableNeolexon
                    style={[styles.listItem]}
                    onPress={() => {
                      setLocalSelectedPatientID(item._id);
                    }}
                  >
                    <View style={[styles.userBox, idStyles.backgroundColor]}>
                      <Image style={styles.userImg} source={image} />
                      <Text
                        numberOfLines={2}
                        ellipsizeMode="tail"
                        style={{
                          color: isSelected ? colors.white : colors.black,
                          maxWidth: '75%',
                        }}
                      >
                        {name}
                      </Text>
                    </View>
                  </PressableNeolexon>
                );
              }}
            />
          </View>
        </View>
      </Modal>
      <Modal
        visible={showCopyErrorModal}
        style={styles.modalAll}
        onClose={() => setShowCopyErrorModal(false)}
        isVisible={showCopyErrorModal}
        confirmActionButton={
          <Button label="OK" onPress={() => setShowCopyErrorModal(false)} />
        }
        title="Fehler beim Kopieren"
      >
        <Text></Text>
      </Modal>
    </>
  );
}

const styles = StyleSheet.create({
  card: { marginHorizontal: 4 },
  controlWrapper: {
    flexDirection: 'row',
    marginTop: 16,
    marginBottom: 4,
    justifyContent: 'center',
  },
  setTitleContainer: { padding: 8 },
  setTitle: {
    textAlign: 'center',
    fontSize: 18,
    fontWeight: '600',
  },
  userImg: {
    width: 30,
    height: 30,
    borderRadius: 60,
    marginRight: 10,
  },
  userBox: {
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'center',
    padding: 10,
  },
  modalAll: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },

  greyBackground: {
    flex: 1,
    backgroundColor: '#00000099',
    position: 'absolute',
    width: Dimensions.get('screen').width,
    height: Dimensions.get('screen').height,
  },
  underline: {
    backgroundColor: colors.grey,
    width: '100%',
    height: 1,
  },
  maxWidth: {
    backgroundColor: colors.white,
    flex: 1,
    maxWidth: 1000,
    maxHeight: '90%',
  },
  titleOne: {
    textAlign: 'center',
    fontWeight: '500',
    color: colors.darkGrey,
    paddingVertical: 15,
  },
  titleTwo: {
    textAlign: 'center',
    fontSize: 18,
    fontWeight: '600',
    paddingVertical: 15,
  },
  containerButtons: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 10,
    gap: 10,
  },
  button: {
    padding: 10,
    flex: 1,
    borderRadius: 4,
  },
  buttonText: {
    textAlign: 'center',
    fontSize: 16,
    fontWeight: '600',
  },
  listItem: {
    margin: 5,
    flex: 1 / 3,
  },
  textStyle: {
    fontSize: 16,
    paddingVertical: 15,
    paddingHorizontal: 25,
  },
});
